import { BN } from '@coral-xyz/anchor';
import { PublicKey } from '@solana/web3.js';

class ClusterError extends Error {
  constructor() {
    super('Cluster not supported');
    Object.setPrototypeOf(this, ClusterError.prototype);
  }
}

export type SugarMoneyClusterMoniker = 'localnet' | 'devnet' | 'mainnet-test' | 'production';

export class SugarMoneyProgramConfig {
  private readonly cluster: SugarMoneyClusterMoniker;

  constructor(cluster: SugarMoneyClusterMoniker = 'devnet') {
    this.cluster = cluster;
  }

  public getCluster(): SugarMoneyClusterMoniker {
    return this.cluster;
  }

  public readonly Precision = new BN('1').shln(64);
  public readonly TokenPrecision = new BN(1_000_000);
  public readonly BpsBase = new BN(10_000);

  public get accountLookupTable(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('739nwTtgWYgJmR6bFzNisxSm3BMv92adu68uLskPVc2N');
      case 'mainnet-test':
      case 'production':
        return new PublicKey('EWazSfqU1Kq8LGRyC422i6nKuErc5yEFmpQsvbBchrGa');
      default:
        throw new ClusterError();
    }
  }

  public get sugarMoneyProgramId(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('Dx4L3VUfwKEkmjJatoEFoYB6FG12Gb5D6iAHkxdeMe5f');
      case 'mainnet-test':
        return new PublicKey('2pbhpVLBKvqKXNbV6V2cvYME1dE9KCb39jZczsZnoTfu');
      case 'production':
        return new PublicKey('deus4Bvftd5QKcEkE5muQaWGWDoma8GrySvPFrBPjhS');
      default:
        throw new ClusterError();
    }
  }

  public get wrapperProgramId(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('425j64guiFchMhb29pnH9qoEQeQW5Fijv9E5PiMxYumJ');
      case 'mainnet-test':
        return new PublicKey('2W8sBc5dskGTdy7X3Sf3cRVksQQH6ePbfFLbMHj29Wiv');
      case 'production':
        return new PublicKey('pBasedTVV99yFBmg2u6Uvka9fJueRcs7SB9PJ2HfrEn');
      default:
        throw new ClusterError();
    }
  }

  public get ammConfigId(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('9zSzfkYy6awexsHvmggeH36pfVUdDGyCcwmjT3AQPBj6');
      case 'production':
      case 'mainnet-test':
        return new PublicKey('D4FPEruKEHrG5TenZ2mpDGEfu1iUvTiqBxvpU8HLBvC2');
      default:
        throw new ClusterError();
    }
  }

  public get raydiumAmmProgramId(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('CPMDWBwJDtYax9qW7AyRuVC19Cc4L4Vcy4n2BHAbHkCW');
      case 'production':
      case 'mainnet-test':
        return new PublicKey('CPMMoo8L3F4NbTegBCKVNunggL7H1ZpdTHKxQB5qKP1C');
      default:
        throw new ClusterError();
    }
  }

  public get raydiumLockingProgramId(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('DLockwT7X7sxtLmGH9g5kmfcjaBtncdbUmi738m5bvQC');
      case 'production':
      case 'mainnet-test':
        return new PublicKey('LockrWmn6K5twhz3y9w1dQERbmgSaRkfnTeTKbpofwE');
      default:
        throw new ClusterError();
    }
  }

  public get raydiumCreateFeeAddress(): PublicKey {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new PublicKey('G11FKBRaAkHAKuLCgLM6K6NUc9rTjPAznRCjZifrTQe2');
      case 'production':
      case 'mainnet-test':
        return new PublicKey('DNXgeM9EiiaAbaWvwjHj9fQQLAX5ZsfHyvmYUNRAdNC8');
      default:
        throw new ClusterError();
    }
  }

  public get wrapperMintKey(): PublicKey {
    switch (this.cluster) {
      case 'devnet':
      case 'localnet':
        return new PublicKey('pSoLv4bKBuQTNg9vqcMytuVeJUZhLQpi2GDPxaSKVTP');
      case 'mainnet-test':
        return new PublicKey('tstd5BAbtB81TG3kwL4zFLF6x73edB2atEnqvUBqqmh');
      case 'production':
        return new PublicKey('pSoL47GE52V2bgUUyQvs9LSdWQZsokarp2yNsWQaLYy');
      default:
        throw new ClusterError();
    }
  }

  public get distributorProgramId(): PublicKey {
    switch (this.cluster) {
      case 'devnet':
      case 'localnet':
        return new PublicKey('2W35i4z7YMrc1ZsvhAW6eKD3kDYerZUcLbyP59vHDLwW');
      case 'mainnet-test':
        return new PublicKey('3UpLNCXwYDK7bKcyZcFZmpbyQNFfFpPPJpRwBpT8yvLV');
      case 'production':
        return new PublicKey('vu1t7ReSFRLfUsnbkQWh1x2JKDVLgecp86zR1iphmDo');
      default:
        throw new ClusterError();
    }
  }

  public get wsolMintAddress(): PublicKey {
    return new PublicKey('So11111111111111111111111111111111111111112');
  }

  public get totalSupply(): BN {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new BN(1_000_000_000_000);
      case 'mainnet-test':
      case 'production':
        return new BN(1_000_000_000_000_000);
      default:
        throw new ClusterError();
    }
  }

  public get totalTokensToSell(): BN {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new BN(780_000_000_000);
      case 'mainnet-test':
      case 'production':
        return new BN(780_000_000_000_000);
      default:
        throw new ClusterError();
    }
  }

  public get tokensToDex(): BN {
    switch (this.cluster) {
      case 'localnet':
      case 'devnet':
        return new BN(220_000_000_000);
      case 'mainnet-test':
      case 'production':
        return new BN(220_000_000_000_000);
      default:
        throw new ClusterError();
    }
  }
}
